import { Check, Clear, DeleteForever, EditOutlined, PlayArrow } from "@mui/icons-material";
import { Button, FormGroup, Grid, IconButton } from "@mui/material";
import gql from "graphql-tag";
import { useState } from "react";
import { useMutation } from "react-apollo";
import { toast } from "react-toastify";
import styled from "styled-components";
import { GetDatacenter_datacenter } from "../../views/App/__generated__/GetDatacenter";
import { TOAST_SETTINGS } from "../BillingDetails/AddPaymentMethodDialog";
import CardHESP from "../CardHESP/CardHESP";
import CardHESPTitle from "../CardHESP/CardHESPTitle";
import DataItem from "../DataItem/DataItem";
import { RunBootstrapsMutation, RunBootstrapsMutationVariables } from "./__generated__/RunBootstrapsMutation";
import ConfirmRunBootstrapsDialog from "./ConfirmRunBootstrapsDialog";
import TLARemoveDatacenterDialog from "./TLARemoveDatacenterDialog";
import TLAUpdateDatacenterIdDialog from "./TLAUpdateDatacenterIdDialog";
import TLAUpdateDatacenterInfoDialog from "./TLAUpdateDatacenterInfoDialog";
import TLAUpdateDatacenterNameDialog from "./TLAUpdateDatacenterNameDialog";

interface Props {
    datacenter: GetDatacenter_datacenter;
}

const TLAContent = styled.div`
    display: flex;
    flex-direction: column;
    border-radius: 8px;
    padding: 10px;
    background-color: ${(props) => props.theme.grayBackground};
    margin-top: 6px;
    margin-bottom: 6px;
    padding: 10px 16px;
`;

const Title = styled.h1`
    font-weight: bolder;
    font-size: 16px;
    margin: 0;
    padding: 0;
    padding-bottom: 4px;
`;

export const RUN_BOOTSTRAPS_MUTATION = gql`
    mutation RunBootstrapsMutation($input: RunBootstrapsInput!) {
        runDatacenterBootstraps(input: $input)
    }
`;

export default function TLADatacenterBasicDetails({ datacenter }: Props): JSX.Element {
    const [openDialog, setOpenDialog] = useState<boolean>(false);

    const [openEditIdDialog, setOpenEditIdDialog] = useState<boolean>(false);
    const [openEditNameDialog, setOpenEditNameDialog] = useState<boolean>(false);
    const [openEditTlaInfoDialog, setOpenEditTlaInfoDialog] = useState<boolean>(false);
    const [openRunBootstrapsDialog, setOpenRunBootstrapsDialog] = useState<boolean>(false);

    const [runBootstrapsMutation] = useMutation<RunBootstrapsMutation, RunBootstrapsMutationVariables>(
        RUN_BOOTSTRAPS_MUTATION,
    );

    async function runBootstraps(): Promise<void> {
        await runBootstrapsMutation({ variables: { input: { id: datacenter.tla.id } } });
        toast.success("Upgrade bootstraps", TOAST_SETTINGS);
        setOpenRunBootstrapsDialog(false);
    }

    return (
        <>
            <CardHESP>
                <CardHESPTitle
                    title="Basic details"
                    button={
                        <div className="flex flex-row gap-1">
                            <Button startIcon={<PlayArrow />} onClick={() => setOpenRunBootstrapsDialog(true)}>
                                Upgrade bootstraps
                            </Button>
                            <Button
                                startIcon={<DeleteForever />}
                                color="error"
                                disabled={datacenter.channels.length > 0}
                                onClick={() => setOpenDialog(true)}
                            >
                                Remove
                            </Button>
                        </div>
                    }
                />
                <Grid container spacing={2}>
                    <Grid item xs={12} md={6}>
                        <FormGroup>
                            <DataItem
                                title="ID"
                                value={datacenter.id}
                                // editable={datacenter.channels.length === 0}
                                // onEditClick={() => setOpenEditIdDialog(true)}
                            />
                        </FormGroup>
                        <FormGroup>
                            <DataItem
                                title="Name"
                                value={datacenter.name}
                                // editable
                                // onEditClick={() => setOpenEditNameDialog(true)}
                            />
                        </FormGroup>
                    </Grid>
                    <Grid item xs={12} md={6}>
                        <TLAContent>
                            <div className="flex flex-row justify-between items-center">
                                <Title>TLA environment info</Title>
                                <IconButton size="small" onClick={() => setOpenEditTlaInfoDialog(true)}>
                                    <EditOutlined fontSize="small" />
                                </IconButton>
                            </div>
                            <FormGroup>
                                <DataItem title="ID" value={datacenter.tla.id} />
                            </FormGroup>
                            <FormGroup>
                                <DataItem title="Name" value={datacenter.tla.name} />
                            </FormGroup>
                            <FormGroup>
                                <DataItem title="Description" value={datacenter.tla.description} />
                            </FormGroup>
                            <div className="flex flex-row gap-2">
                                <FormGroup className="w-full">
                                    <DataItem title="Domain" value={datacenter.tla.domain} />
                                </FormGroup>
                                <FormGroup className="w-full">
                                    <DataItem title="Host ID" value={datacenter.tla.hostId} />
                                </FormGroup>
                            </div>

                            <div className="flex flex-row gap-2">
                                <FormGroup className="w-full">
                                    <DataItem title="Region" value={datacenter.tla.region} />
                                </FormGroup>
                                <FormGroup className="w-full">
                                    <DataItem title="Provider" value={datacenter.tla.provider} />
                                </FormGroup>
                            </div>

                            <div className="flex flex-row gap-2">
                                <FormGroup className="w-full">
                                    <DataItem
                                        title="HTTP3"
                                        value={
                                            datacenter.tla.http3 === true ? (
                                                <Check color="success" />
                                            ) : (
                                                <Clear color="error" />
                                            )
                                        }
                                    />
                                </FormGroup>
                                <FormGroup className="w-full">
                                    <DataItem
                                        title="TLS"
                                        value={
                                            datacenter.tla.tls === true ? (
                                                <Check color="success" />
                                            ) : (
                                                <Clear color="error" />
                                            )
                                        }
                                    />
                                </FormGroup>
                            </div>

                            {datacenter.tla.override && (
                                <FormGroup>
                                    <DataItem
                                        title="Override"
                                        value={JSON.stringify(JSON.parse(datacenter.tla.override), null, 2)}
                                    />
                                </FormGroup>
                            )}
                            {datacenter.tla.fqdn !== null && (
                                <>
                                    <FormGroup>
                                        <DataItem
                                            title="FQDN (RTMP push enabled)"
                                            value={
                                                <div className="flex flex-col">
                                                    <div>Ingest: {datacenter.tla.fqdn.ingest}</div>
                                                    <div>Origins: {datacenter.tla.fqdn.origins}</div>
                                                </div>
                                            }
                                        />
                                    </FormGroup>
                                </>
                            )}
                            <div className="flex flex-row gap-2">
                                <FormGroup className="w-full">
                                    <DataItem
                                        title="LetsEncrypt"
                                        value={
                                            datacenter.tla.letsEncrypt === true ? (
                                                <Check color="success" />
                                            ) : (
                                                <Clear color="error" />
                                            )
                                        }
                                    />
                                </FormGroup>

                                <FormGroup className="w-full">
                                    <DataItem
                                        title="On-Prem instance?"
                                        value={
                                            datacenter.tla.labels.includes('live.theostream.features=on-prem') ? (
                                                <Check color="success" />
                                            ) : (
                                                <Clear color="error" />
                                            )
                                        }
                                    />
                                </FormGroup>
                            </div>
                            {datacenter.tla.letsEncrypt !== true && datacenter.tla.certificates && (
                                <div className="flex w-full">
                                    <FormGroup className="w-full">
                                        <DataItem
                                            title="Certificates"
                                            value={
                                                <ul>
                                                    {datacenter.tla.certificates.map((cert, i) => (
                                                        <li key={`cert$${i}`} className="text-xs">
                                                            {cert.name} ({cert.source})
                                                        </li>
                                                    ))}
                                                </ul>
                                            }
                                        />
                                    </FormGroup>
                                </div>
                            )}
                            <FormGroup className="w-full">
                                <DataItem
                                    title="Labels"
                                    value={
                                        <ul>
                                            {datacenter.tla.labels.map((label, i) => (
                                                <li
                                                    key={`label-${i}`}
                                                    style={{ margin: 0, padding: 0, paddingLeft: "28px" }}
                                                >
                                                    {label}
                                                </li>
                                            ))}
                                        </ul>
                                    }
                                />
                            </FormGroup>
                        </TLAContent>
                    </Grid>
                </Grid>
            </CardHESP>
            <TLARemoveDatacenterDialog
                datacenterId={datacenter.id}
                name={datacenter.name}
                onClose={() => setOpenDialog(false)}
                open={openDialog}
            />
            <TLAUpdateDatacenterIdDialog
                open={openEditIdDialog}
                onClose={() => setOpenEditIdDialog(false)}
                id={datacenter.id}
            />
            <TLAUpdateDatacenterNameDialog
                open={openEditNameDialog}
                onClose={() => setOpenEditNameDialog(false)}
                id={datacenter.id}
                name={datacenter.name}
            />
            <TLAUpdateDatacenterInfoDialog
                id={datacenter.id}
                open={openEditTlaInfoDialog}
                onClose={() => setOpenEditTlaInfoDialog(false)}
                datacenter={datacenter}
            />
            <ConfirmRunBootstrapsDialog
                open={openRunBootstrapsDialog}
                onClose={() => setOpenRunBootstrapsDialog(false)}
                type="datacenter"
                onConfirm={() => runBootstraps()}
            />
        </>
    );
}
